import { Button } from '@/components/ui-kit/button';
import { cn } from '@/lib/utils';
import type { TemplateField } from '@/modules/templates/types/templates.types';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlignLeft,
  Building2,
  Calendar,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  DollarSign,
  FileText,
  GripVertical,
  Hash,
  Link2,
  List,
  Mail,
  MapPin,
  Percent,
  Phone,
  Plus,
  Sparkles,
  Trash2,
  Type,
  Users,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBuilder, type ScreenWithFields } from './typeform-builder';

// Field type icons
const fieldTypeIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  text: Type,
  email: Mail,
  date: Calendar,
  number: Hash,
  checkbox: CheckSquare,
  select: List,
  multiselect: List,
  textarea: AlignLeft,
  phone: Phone,
  address: MapPin,
  party: Building2,
  currency: DollarSign,
  percentage: Percent,
  url: Link2,
};

// Screen type icons
const screenTypeIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  standard: FileText,
  dynamic: Sparkles,
  signatory: Users,
};

interface SortableFieldItemProps {
  field: TemplateField;
  screenId: string;
  isSelected: boolean;
  onSelect?: () => void;
}

function SortableFieldItem({ field, screenId, isSelected, onSelect }: SortableFieldItemProps) {
  const { setSelection, deleteField } = useBuilder();
  const Icon = fieldTypeIcons[field.type] || Type;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all ml-6',
        isSelected ? 'bg-selise-blue/10 text-selise-blue' : 'text-[hsl(var(--fg))] hover:bg-muted'
      )}
      onClick={() => {
        setSelection({ type: 'field', screenId, fieldId: field.id });
        onSelect?.();
      }}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <GripVertical className="h-3 w-3 text-globe-grey" />
      </div>
      <Icon
        className={cn('h-4 w-4 shrink-0', isSelected ? 'text-selise-blue' : 'text-globe-grey')}
      />
      <span className="text-sm truncate flex-1">{field.label}</span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          deleteField(field.id);
        }}
        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-destructive/10 rounded transition-all"
      >
        <Trash2 className="h-3 w-3 text-destructive" />
      </button>
    </div>
  );
}

interface SortableScreenItemProps {
  screen: ScreenWithFields;
  index: number;
  onSelect?: () => void;
}

function SortableScreenItem({ screen, index, onSelect }: SortableScreenItemProps) {
  const { selection, setSelection, deleteScreen, addField, reorderFields } = useBuilder();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  const isScreenSelected = selection.type === 'screen' && selection.screenId === screen.id;
  const hasSelectedField = selection.screenId === screen.id && selection.type === 'field';

  const ScreenIcon = screenTypeIcons[(screen as any).type || 'standard'] || FileText;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: screen.id,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleFieldDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = screen.fields.findIndex((f) => f.id === active.id);
    const newIndex = screen.fields.findIndex((f) => f.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(
      screen.fields.map((f) => f.id),
      oldIndex,
      newIndex
    );
    reorderFields(screen.id, newOrder);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="select-none">
      {/* Screen header */}
      <div
        className={cn(
          'group flex items-center gap-2 px-2 py-2.5 rounded-lg cursor-pointer transition-all',
          isScreenSelected || hasSelectedField ? 'bg-selise-blue/5' : 'hover:bg-muted'
        )}
        onClick={() => {
          setSelection({ type: 'screen', screenId: screen.id, fieldId: null });
          onSelect?.();
        }}
      >
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <GripVertical className="h-4 w-4 text-globe-grey" />
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          className="p-0.5 hover:bg-muted rounded"
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-globe-grey" />
          ) : (
            <ChevronRight className="h-4 w-4 text-globe-grey" />
          )}
        </button>
        <div
          className={cn(
            'flex items-center justify-center h-6 w-6 rounded text-xs font-medium',
            isScreenSelected
              ? 'bg-selise-blue text-[hsl(var(--white))]'
              : 'bg-muted text-globe-grey'
          )}
        >
          {index + 1}
        </div>
        <span
          className={cn(
            'text-sm font-medium truncate flex-1',
            isScreenSelected ? 'text-selise-blue' : 'text-[hsl(var(--fg))]'
          )}
        >
          {screen.title}
        </span>
        <ScreenIcon className="h-4 w-4 text-[hsl(var(--white))] opacity-0 group-hover:opacity-100" />
        <button
          onClick={(e) => {
            e.stopPropagation();
            deleteScreen(screen.id);
          }}
          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-destructive/10 rounded transition-all"
        >
          <Trash2 className="h-3 w-3 text-destructive" />
        </button>
      </div>

      {/* Fields */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleFieldDragEnd}
            >
              <SortableContext
                items={screen.fields.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="py-1 space-y-0.5">
                  {screen.fields.map((field) => (
                    <SortableFieldItem
                      key={field.id}
                      field={field}
                      screenId={screen.id}
                      isSelected={selection.fieldId === field.id}
                      onSelect={onSelect}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {/* Add field button */}
            <button
              onClick={() => addField(screen.id)}
              className="flex items-center gap-2 px-3 py-2 ml-6 text-sm text-globe-grey hover:text-selise-blue hover:bg-selise-blue/5 rounded-lg transition-colors w-[calc(100%-1.5rem)]"
            >
              <Plus className="h-4 w-4" />
              <span>{t('TEMPLATEBUILDER_NAVIGATIONPANEL_ADDFIELD')}</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile drill-down navigation (chat-app pattern).
//
// On mobile the drawer can't show a nested expand/collapse tree comfortably,
// so we present a 2-step wizard inside the drawer:
//   • Layer 1: flat list of screens (tap to drill into that screen's fields)
//   • Layer 2: flat list of fields for the drilled screen (with back button)
// Picking a field selects it and closes the drawer — the canvas (layer 3 in
// the user's mental model) takes over the viewport.
// ---------------------------------------------------------------------------

interface MobileScreenRowProps {
  screen: ScreenWithFields;
  index: number;
  isActive: boolean;
  onTap: () => void;
  onDelete: () => void;
}

function MobileScreenRow({ screen, index, isActive, onTap, onDelete }: MobileScreenRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: screen.id,
  });
  const ScreenIcon = screenTypeIcons[(screen as any).type || 'standard'] || FileText;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 px-2 py-3 rounded-lg cursor-pointer transition-colors',
        isActive ? 'bg-selise-blue/10' : 'hover:bg-muted active:bg-muted'
      )}
      onClick={onTap}
    >
      <button
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        className="cursor-grab active:cursor-grabbing touch-none p-1 -ml-1"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4 text-globe-grey" />
      </button>
      <div
        className={cn(
          'flex items-center justify-center h-7 w-7 rounded text-xs font-medium shrink-0',
          isActive ? 'bg-selise-blue text-[hsl(var(--white))]' : 'bg-muted text-globe-grey'
        )}
      >
        {index + 1}
      </div>
      <ScreenIcon
        className={cn('h-4 w-4 shrink-0', isActive ? 'text-selise-blue' : 'text-globe-grey')}
      />
      <span
        className={cn(
          'text-sm font-medium truncate flex-1',
          isActive ? 'text-selise-blue' : 'text-[hsl(var(--fg))]'
        )}
      >
        {screen.title}
      </span>
      <span className="text-xs text-globe-grey shrink-0">{screen.fields.length}</span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        aria-label="Delete screen"
        className="p-1.5 hover:bg-destructive/10 rounded transition-colors"
      >
        <Trash2 className="h-4 w-4 text-destructive" />
      </button>
      <ChevronRight className="h-5 w-5 text-globe-grey shrink-0" />
    </div>
  );
}

interface MobileFieldRowProps {
  field: TemplateField;
  isActive: boolean;
  onTap: () => void;
  onDelete: () => void;
}

function MobileFieldRow({ field, isActive, onTap, onDelete }: MobileFieldRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });
  const Icon = fieldTypeIcons[field.type] || Type;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 px-2 py-3 rounded-lg cursor-pointer transition-colors',
        isActive ? 'bg-selise-blue/10' : 'hover:bg-muted active:bg-muted'
      )}
      onClick={onTap}
    >
      <button
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        className="cursor-grab active:cursor-grabbing touch-none p-1 -ml-1"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4 text-globe-grey" />
      </button>
      <Icon className={cn('h-4 w-4 shrink-0', isActive ? 'text-selise-blue' : 'text-globe-grey')} />
      <span
        className={cn(
          'text-sm truncate flex-1',
          isActive ? 'text-selise-blue font-medium' : 'text-[hsl(var(--fg))]'
        )}
      >
        {field.label}
      </span>
      {field.required && <span className="text-destructive text-xs shrink-0">*</span>}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        aria-label="Delete field"
        className="p-1.5 hover:bg-destructive/10 rounded transition-colors"
      >
        <Trash2 className="h-4 w-4 text-destructive" />
      </button>
    </div>
  );
}

// Inline-editable screen title + description, mirroring the canvas's
// click-to-edit pattern so the L2 fields list still lets the user rename
// or describe the screen without diving back to a desktop view.
function MobileScreenHeader({ screen }: { screen: ScreenWithFields }) {
  const { updateScreen } = useBuilder();
  const { t } = useTranslation();
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [title, setTitle] = useState(screen.title);
  const [desc, setDesc] = useState(screen.description || '');
  const titleRef = useRef<HTMLInputElement>(null);
  const descRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTitle(screen.title);
    setDesc(screen.description || '');
  }, [screen.id, screen.title, screen.description]);

  useEffect(() => {
    if (editingTitle) {
      titleRef.current?.focus();
      titleRef.current?.select();
    }
  }, [editingTitle]);

  useEffect(() => {
    if (editingDesc) descRef.current?.focus();
  }, [editingDesc]);

  const saveTitle = () => {
    setEditingTitle(false);
    if (title !== screen.title) updateScreen(screen.id, { title });
  };

  const saveDesc = () => {
    setEditingDesc(false);
    if (desc !== (screen.description || '')) {
      updateScreen(screen.id, { description: desc || undefined });
    }
  };

  return (
    <div className="p-4 border-b border-border">
      {editingTitle ? (
        <input
          ref={titleRef}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={saveTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveTitle();
            if (e.key === 'Escape') {
              setEditingTitle(false);
              setTitle(screen.title);
            }
          }}
          className="w-full text-xl font-semibold text-[hsl(var(--fg))] bg-transparent border-none outline-none focus:ring-0 placeholder:text-globe-grey/50"
          placeholder={t('TEMPLATEBUILDER_PREVIEW_SCREENTITLEPLACEHOLDER')}
        />
      ) : (
        <h2
          onClick={() => setEditingTitle(true)}
          className="text-xl font-semibold text-[hsl(var(--fg))] cursor-text hover:bg-muted/50 rounded px-1 -mx-1 py-0.5 transition-colors"
        >
          {title || t('TEMPLATEBUILDER_PREVIEW_SCREENTITLEPLACEHOLDER')}
        </h2>
      )}
      {editingDesc ? (
        <input
          ref={descRef}
          type="text"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          onBlur={saveDesc}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveDesc();
            if (e.key === 'Escape') {
              setEditingDesc(false);
              setDesc(screen.description || '');
            }
          }}
          className="mt-1 w-full text-sm text-globe-grey bg-transparent border-none outline-none focus:ring-0 placeholder:text-globe-grey/50"
          placeholder={t('TEMPLATEBUILDER_PREVIEW_DESCRIPTIONPLACEHOLDER')}
        />
      ) : (
        <p
          onClick={() => setEditingDesc(true)}
          className={cn(
            'mt-1 text-sm cursor-text hover:bg-muted/50 rounded px-1 -mx-1 py-0.5 transition-colors',
            desc ? 'text-globe-grey' : 'text-globe-grey/50 italic'
          )}
        >
          {desc || t('TEMPLATEBUILDER_PREVIEW_DESCRIPTIONPLACEHOLDER')}
        </p>
      )}
    </div>
  );
}

interface MobileNavigationPanelProps {
  /** Which layer of the wizard is showing. Controlled by parent so the
      global header's back button can drive layer-up navigation. */
  view: 'screens' | 'fields';
  onViewChange: (view: 'screens' | 'fields') => void;
  /** Called after a field is tapped — parent should switch to the canvas
      layer (L3) so the user can edit that field. */
  onDrillToCanvas: () => void;
}

function MobileNavigationPanel({
  view,
  onViewChange,
  onDrillToCanvas,
}: MobileNavigationPanelProps) {
  const {
    screens,
    selection,
    setSelection,
    addScreen,
    addField,
    deleteScreen,
    deleteField,
    reorderScreens,
    reorderFields,
  } = useBuilder();
  const { t } = useTranslation();

  // The drilled screen is whichever screen is currently selected. Tapping a
  // screen in L1 sets selection, then L2 reads it back here.
  const drilledScreen = selection.screenId
    ? screens.find((s) => s.id === selection.screenId)
    : null;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleScreenDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = screens.findIndex((s) => s.id === active.id);
    const newIndex = screens.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    reorderScreens(
      arrayMove(
        screens.map((s) => s.id),
        oldIndex,
        newIndex
      )
    );
  };

  const handleFieldDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !drilledScreen) return;
    const fields = drilledScreen.fields;
    const oldIndex = fields.findIndex((f) => f.id === active.id);
    const newIndex = fields.findIndex((f) => f.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    reorderFields(
      drilledScreen.id,
      arrayMove(
        fields.map((f) => f.id),
        oldIndex,
        newIndex
      )
    );
  };

  // Layer 1: screens list
  if (view === 'screens' || !drilledScreen) {
    return (
      <div className="flex flex-col h-full">
        {/* <div className="px-4 h- flex items-center border-b border-border shrink-0">
          <h2 className="text-base font-semibold text-[hsl(var(--fg))]">
            {t('TEMPLATEBUILDER_NAVIGATIONPANEL_SCREENS', 'Screens')}
          </h2>
        </div> */}

        <div className="flex-1 overflow-y-auto p-2">
          {screens.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="h-10 w-10 text-globe-grey opacity-50 mb-3" />
              <p className="text-sm text-globe-grey">
                {t('TEMPLATEBUILDER_NAVIGATIONPANEL_NOSCREENSYET')}
              </p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleScreenDragEnd}
            >
              <SortableContext
                items={screens.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-1">
                  {screens.map((screen, index) => (
                    <MobileScreenRow
                      key={screen.id}
                      screen={screen}
                      index={index}
                      isActive={selection.screenId === screen.id}
                      onTap={() => {
                        setSelection({
                          type: 'screen',
                          screenId: screen.id,
                          fieldId: null,
                        });
                        onViewChange('fields');
                      }}
                      onDelete={() => deleteScreen(screen.id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        <div className="p-3 border-t border-border shrink-0">
          <Button onClick={addScreen} variant="primary" className="w-full" size="sm">
            <Plus className="h-4 w-4" />
            {t('TEMPLATEBUILDER_NAVIGATIONPANEL_ADDCONTENT')}
          </Button>
        </div>
      </div>
    );
  }

  // Layer 2: fields of the drilled screen. Back is handled by the global
  // header (BuilderHeader). The screen's title + description sit at the top
  // of the scrollable area as inline-editable fields so the user can rename
  // / re-describe the screen without leaving this layer.
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        <MobileScreenHeader screen={drilledScreen} />

        <div className="p-2">
          {drilledScreen.fields.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="h-10 w-10 text-globe-grey opacity-50 mb-3" />
              <p className="text-sm text-globe-grey">
                {t('TEMPLATEBUILDER_NAVIGATIONPANEL_NOFIELDSYET', 'No fields yet')}
              </p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleFieldDragEnd}
            >
              <SortableContext
                items={drilledScreen.fields.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-1">
                  {drilledScreen.fields.map((field) => (
                    <MobileFieldRow
                      key={field.id}
                      field={field}
                      isActive={selection.fieldId === field.id}
                      onTap={() => {
                        setSelection({
                          type: 'field',
                          screenId: drilledScreen.id,
                          fieldId: field.id,
                        });
                        onDrillToCanvas(); // L2 → L3
                      }}
                      onDelete={() => deleteField(field.id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      <div className="p-3 border-t border-border shrink-0">
        <Button
          onClick={() => addField(drilledScreen.id)}
          variant="primary"
          className="w-full"
          size="sm"
        >
          <Plus className="h-4 w-4" />
          {t('TEMPLATEBUILDER_NAVIGATIONPANEL_ADDFIELD')}
        </Button>
      </div>
    </div>
  );
}

interface NavigationPanelProps {
  /** Mobile mode renders the chat-app drill (L1 screens → L2 fields).
      Parent controls which layer is showing via `mobileView` so the global
      header's back button can drive layer-up navigation. */
  isMobile?: boolean;
  mobileView?: 'screens' | 'fields';
  onMobileViewChange?: (view: 'screens' | 'fields') => void;
  /** Called when the user taps a field in L2 — parent should switch to L3
      (the canvas). */
  onDrillToCanvas?: () => void;
}

export function NavigationPanel({
  isMobile = false,
  mobileView = 'screens',
  onMobileViewChange,
  onDrillToCanvas,
}: NavigationPanelProps = {}) {
  if (isMobile) {
    return (
      <MobileNavigationPanel
        view={mobileView}
        onViewChange={onMobileViewChange ?? (() => {})}
        onDrillToCanvas={onDrillToCanvas ?? (() => {})}
      />
    );
  }
  return <DesktopNavigationPanel />;
}

function DesktopNavigationPanel() {
  const { screens, addScreen, reorderScreens } = useBuilder();
  const { t } = useTranslation();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleScreenDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = screens.findIndex((s) => s.id === active.id);
    const newIndex = screens.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(
      screens.map((s) => s.id),
      oldIndex,
      newIndex
    );
    reorderScreens(newOrder);
  };

  return (
    <aside className="w-64 border-r border-border bg-[hsl(var(--bg))] flex flex-col shrink-0">
      {/* Add content button */}
      <div className="p-3 border-b border-border">
        <Button onClick={addScreen} variant="primary" className="w-full" size={'sm'}>
          <Plus className="h-4 w-4" />
          {t('TEMPLATEBUILDER_NAVIGATIONPANEL_ADDCONTENT')}
        </Button>
      </div>

      {/* Screen list */}
      <div className="flex-1 overflow-y-auto p-2">
        {screens.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <FileText className="h-10 w-10 text-globe-grey opacity-50 mb-3" />
            <p className="text-sm text-globe-grey">
              {t('TEMPLATEBUILDER_NAVIGATIONPANEL_NOSCREENSYET')}
            </p>
            <p className="text-xs text-globe-grey mt-1">
              {t('TEMPLATEBUILDER_NAVIGATIONPANEL_CLICKADDCONTENTTOGETSTARTED', {
                action: t('TEMPLATEBUILDER_NAVIGATIONPANEL_ADDCONTENT'),
              })}
            </p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleScreenDragEnd}
          >
            <SortableContext
              items={screens.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-1">
                {screens.map((screen, index) => (
                  <SortableScreenItem key={screen.id} screen={screen} index={index} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </aside>
  );
}
